import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import OperatorsManager from "@/components/app/staff/OperatorsManager";
import PayoutsCard from "@/components/app/staff/PayoutsCard";
import PartnerEnquiries from "@/components/app/staff/PartnerEnquiries";
import BannerApprovals from "@/components/app/staff/BannerApprovals";
import BannerPricingForm from "@/components/app/staff/BannerPricingForm";
import { Check, X, Trash2, Store, TrendingDown, ShieldCheck, Hourglass, Gift, Mail, Bell, Sparkles, Eraser } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState as useLocalState } from "react";

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className={`rounded-2xl p-5 border ${tint}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</div>
          <div className="mt-1 font-display font-black text-3xl">{value ?? "—"}</div>
        </div>
        <Icon className="w-8 h-8 opacity-70" />
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [creditFor, setCreditFor] = useLocalState(null);
  const [creditAmount, setCreditAmount] = useLocalState(5);
  const [creditNote, setCreditNote] = useLocalState("");
  const [creditBusy, setCreditBusy] = useLocalState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/admin/stores"), api.get("/admin/stats")]);
      setStores(a.data);
      setStats(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/stores/${id}/approve`); toast.success("Store approved"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/stores/${id}/reject`); toast.success("Store set to pending"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Permanently delete this store and all its markdowns?")) return;
    try { await api.delete(`/admin/stores/${id}`); toast.success("Store deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const grantCredit = async () => {
    if (!creditFor) return;
    setCreditBusy(true);
    try {
      const { data } = await api.post(`/admin/stores/${creditFor.id}/credit`, { amount: Number(creditAmount), note: creditNote });
      toast.success(`Credited $${Number(creditAmount).toFixed(2)} to ${creditFor.store_name} · new balance $${data.new_balance.toFixed(2)}`);
      setCreditFor(null); setCreditAmount(5); setCreditNote("");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setCreditBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-900">Admin panel</h1>
        <p className="text-slate-600 mt-1">Approve stores, moderate deals, keep Blurblive tidy.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={async()=>{ try { const {data}=await api.post("/admin/send-digest"); toast.success(`Digest emailed · ${data.counts.new_markdowns} markdowns, $${data.counts.revenue.toFixed(2)} revenue`); } catch(e){ toast.error("Send failed"); } }} className="bg-emerald-800 hover:bg-emerald-900" data-testid="send-digest-btn"><Mail className="w-4 h-4 mr-1.5" />Send weekly digest to me</Button>
        <Button onClick={async()=>{ try { const {data}=await api.post("/admin/send-expiry-reminders"); toast.success(`${data.sent} reminders sent`); } catch(e){ toast.error("Send failed"); } }} variant="outline" data-testid="send-reminders-btn"><Bell className="w-4 h-4 mr-1.5" />Send expiry reminders</Button>
        <Button onClick={async()=>{ try { toast.loading("Seeding demo data…", {id:"seed"}); const {data}=await api.post("/admin/seed-demo"); toast.success(`Seeded ${data.stores_added} stores · ${data.items_added} markdowns`, {id:"seed"}); load(); } catch(e){ toast.error("Seed failed: " + formatError(e.response?.data?.detail), {id:"seed"}); } }} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="seed-demo-btn"><Sparkles className="w-4 h-4 mr-1.5" />Seed demo data</Button>
        <Button onClick={async()=>{ if(!window.confirm("Wipe ALL demo stores + markdowns from production?")) return; try { const {data}=await api.post("/admin/wipe-demo"); toast.success(`Wiped ${data.wiped} demo stores`); load(); } catch(e){ toast.error("Wipe failed"); } }} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="wipe-demo-btn"><Eraser className="w-4 h-4 mr-1.5" />Wipe demo data</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Store} label="Stores total" value={stats.stores_total} tint="bg-emerald-50 border-emerald-200 text-emerald-900" />
        <StatCard icon={Hourglass} label="Pending approval" value={stats.stores_pending} tint="bg-amber-50 border-amber-200 text-amber-900" />
        <StatCard icon={ShieldCheck} label="Approved" value={stats.stores_approved} tint="bg-white border-slate-200 text-slate-900" />
        <StatCard icon={TrendingDown} label="Active markdowns" value={stats.markdowns_active} tint="bg-red-50 border-red-200 text-red-900" />
      </div>

      <OperatorsManager />
      <PayoutsCard admin />
      <div className="grid lg:grid-cols-2 gap-6">
        <BannerApprovals />
        <BannerPricingForm />
      </div>
      <PartnerEnquiries />

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Store accounts</CardTitle>
          <CardDescription>Approve new stores so they can start posting markdowns.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-slate-500">Loading…</div> : (
            <div className="space-y-2" data-testid="admin-stores-list">
              {stores.length === 0 && <div className="text-slate-500 text-sm">No stores yet.</div>}
              {stores.map(s => (
                <div key={s.id} className="flex flex-col sm:flex-row sm:items-center gap-3 border border-slate-200 rounded-xl p-3" data-testid={`admin-store-${s.id}`}>
                  <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-900 grid place-items-center font-display font-black">{(s.store_name||"?").charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {s.store_name}
                      {s.approved ? <Badge className="bg-emerald-700 hover:bg-emerald-700">Approved</Badge> : <Badge variant="outline" className="border-amber-400 text-amber-800">Pending</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{s.email} · {s.region}{s.phone && ` · ${s.phone}`}</div>
                    <div className="text-[11px] text-emerald-800 font-semibold mt-0.5">Wallet: ${Number(s.wallet_balance_nzd || 0).toFixed(2)} · {Number(s.listing_credits || 0)} listing credits</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {!s.approved ? (
                      <Button size="sm" onClick={()=>approve(s.id)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`approve-${s.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={()=>reject(s.id)} data-testid={`unapprove-${s.id}`}><X className="w-4 h-4 mr-1" />Block</Button>
                    )}
                    <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" onClick={()=>setCreditFor(s)} data-testid={`credit-${s.id}`}><Gift className="w-4 h-4 mr-1" />Credit</Button>
                    <Button size="sm" variant="outline" className="text-red-700 hover:text-red-800 border-red-200" onClick={()=>del(s.id)} data-testid={`delete-store-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!creditFor} onOpenChange={(v)=>!v && setCreditFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Gift className="w-5 h-5 text-amber-600" />Award credit</DialogTitle>
            <DialogDescription>{creditFor?.store_name} · current balance ${Number(creditFor?.wallet_balance_nzd || 0).toFixed(2)} NZD</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              {[2, 5, 10, 20].map(p => (
                <button key={p} onClick={()=>setCreditAmount(p)} className={`h-11 rounded-lg border font-display font-bold text-sm ${Number(creditAmount)===p ? "bg-amber-500 text-white border-amber-500" : "bg-white border-slate-200 hover:border-amber-300"}`} data-testid={`credit-preset-${p}`}>${p}</button>
              ))}
            </div>
            <div>
              <Label>Custom amount (NZD)</Label>
              <Input type="number" step="0.5" min="0.5" max="500" value={creditAmount} onChange={(e)=>setCreditAmount(e.target.value)} data-testid="credit-amount" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={creditNote} onChange={(e)=>setCreditNote(e.target.value)} placeholder="e.g. Launch bonus, apology, promo" data-testid="credit-note" />
            </div>
            <Button onClick={grantCredit} disabled={creditBusy} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-11" data-testid="credit-submit">
              {creditBusy ? "Awarding…" : `Award $${Number(creditAmount||0).toFixed(2)} credit`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
